/**
 * Phase I Wave 2.5 · <FeatureGate> · UX lock overlay for plan-gated UI.
 * ─────────────────────────────────────────────────────────────────────
 * Wraps any children with a semi-transparent lock + upsell CTA when the
 * current user's plan is below the required tier. If the feature is
 * accessible (plan OK OR admin bypass), renders the children untouched.
 *
 * Usage:
 *   <FeatureGate featureKey="seo_master_base">
 *     <SeoDashboardShell />
 *   </FeatureGate>
 *
 * Custom fallback:
 *   <FeatureGate featureKey="ai_agents_full" fallback={<MyLock />}>...</FeatureGate>
 *
 * Admin bypass (R2)
 * -----------------
 * Users with role `admin` / `super_admin` always pass regardless of plan.
 * See `data/plan_feature_gating.js::userEffectivePlan()`.
 */
import React from "react";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canUseFeature, requiredPlanFor } from "@/data/plan_feature_gating";

/** Build a shallow user doc containing BOTH plan sources AND role so
 *  the gating helpers can apply the admin bypass. */
function _buildUserDoc({ user, plan }) {
    const role = user && user.role;
    return {
        role: role,
        plan: (plan && plan.id) || (user && user.plan) || (user && user.plan_id),
        plan_id: (user && user.plan_id) || (plan && plan.id),
    };
}

export function FeatureGate({ featureKey, children, fallback = null, upgradeHref = "/pricing" }) {
    const { user, plan } = useAuth();
    const userDoc = _buildUserDoc({ user, plan });
    const allowed = canUseFeature(userDoc, featureKey);

    if (allowed) return <>{children}</>;

    if (fallback !== null && fallback !== undefined) return <>{fallback}</>;

    const required = requiredPlanFor(featureKey) || "upgrade";
    const requiredLabel = required.toUpperCase();

    return (
        <div
            className="relative"
            data-testid={`feature-gate-${featureKey}`}
            data-feature-key={featureKey}
            data-required-plan={required}
        >
            <div
                className="opacity-30 pointer-events-none select-none"
                aria-hidden="true"
            >
                {children}
            </div>
            <div
                className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm rounded-lg"
                data-testid={`feature-gate-overlay-${featureKey}`}
            >
                <div className="text-center px-6 py-4 rounded-xl border border-white/10 bg-[#0F172A]/80 shadow-xl">
                    <Lock className="w-8 h-8 mx-auto mb-2 text-[#94A3B8]" aria-hidden="true" />
                    <p
                        className="text-sm font-medium text-white mb-1"
                        data-testid={`feature-gate-required-${featureKey}`}
                    >
                        Available on {requiredLabel}+
                    </p>
                    <a
                        href={upgradeHref}
                        className="text-sm text-[#3B82F6] hover:underline"
                        data-testid={`feature-gate-upgrade-${featureKey}`}
                    >
                        Upgrade now →
                    </a>
                </div>
            </div>
        </div>
    );
}

export default FeatureGate;
